import { useMemo } from "react";

import { useSelector } from "react-redux";

import {
    selectConferenceSnapshotContactParticipantForTask,
    selectConferenceSnapshotActiveParticipants,
    selectConferenceSnapshotSelf,
} from "Services/state/conferences/ConferenceSnapshotSelectors";
import { isPendingInboundConferenceTask } from "Services/Task.service";
import { CALL_STATUS_EVENT } from "Types/CallStatusEvent";

import type { RVTask } from "@regal-voice/shared-types";

export type UseConferenceParticipantsOptions = {
    task?: RVTask;
};

export const useConferenceParticipants = ({ task }: UseConferenceParticipantsOptions = {}) => {
    const snapshotContactParticipant = useSelector(selectConferenceSnapshotContactParticipantForTask)(task);
    const snapshotActiveParticipants = useSelector(selectConferenceSnapshotActiveParticipants);
    const snapshotSelf = useSelector(selectConferenceSnapshotSelf);
    const snapshotConnected =
        snapshotContactParticipant?.status == CALL_STATUS_EVENT.IN_PROGRESS &&
        snapshotSelf?.status == CALL_STATUS_EVENT.IN_PROGRESS;

    const snapshotStatus = useMemo(() => {
        if (!snapshotActiveParticipants?.length) {
            return task && isPendingInboundConferenceTask(task) ? "Ringing..." : "Connecting...";
        }

        const status = snapshotContactParticipant?.status;

        if (status === "ringing" || status == "queued" || snapshotSelf?.status === "ringing") {
            return "Ringing...";
        }

        if (status === "in-progress") {
            // todo. do we want to return "Live" here? like we do in the task card
            // or we might want to return the elapsed time instead of 0 which is sort of wrong.
            return "00:00";
        }

        return "Connecting...";
    }, [snapshotActiveParticipants?.length, snapshotContactParticipant?.status, snapshotSelf?.status, task]);

    return {
        selfParticpant: snapshotSelf,
        activeParticipants: snapshotActiveParticipants,
        contactParticipant: snapshotContactParticipant,
        statusText: snapshotStatus,
        connected: snapshotConnected,
    };
};
